import React, { useState, useEffect } from 'react';
import { salesAPI } from '../../services/api';
import { Sale, SaleStatus, PaymentMethod } from '../../types';
import {
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useCompany } from '../CompanyContext';

interface SubscriptionPaymentsProps {
  subscriptionId: string;
  refreshKey?: number;
}

const SubscriptionPayments: React.FC<SubscriptionPaymentsProps> = ({ subscriptionId, refreshKey }) => {
  const [payments, setPayments] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedCompany } = useCompany();

  useEffect(() => {
    loadPayments();
  }, [subscriptionId, refreshKey, selectedCompany]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const paymentsData = await salesAPI.getBySubscription(subscriptionId, selectedCompany?.id as string);
      setPayments(paymentsData);
    } catch (err) {
      setError('Failed to load subscription payments');
      console.error('Error loading subscription payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case SaleStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case SaleStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case SaleStatus.REFUNDED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: SaleStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case SaleStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case SaleStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case SaleStatus.CANCELLED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case SaleStatus.REFUNDED:
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getPaymentMethodIcon = (method: PaymentMethod) => {
    switch (method) {
      case PaymentMethod.CASH:
        return <CurrencyDollarIcon className="h-4 w-4" />;
      case PaymentMethod.CREDIT_CARD:
      case PaymentMethod.DEBIT_CARD:
        return <DocumentTextIcon className="h-4 w-4" />;
      case PaymentMethod.BANK_TRANSFER:
        return <DocumentTextIcon className="h-4 w-4" />;
      case PaymentMethod.DIGITAL_WALLET:
        return <DocumentTextIcon className="h-4 w-4" />;
      default:
        return <CurrencyDollarIcon className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600">
          <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CurrencyDollarIcon className="h-5 w-5 mr-2" />
          Subscription Payments
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {payments.length} payment{payments.length !== 1 ? 's' : ''} made to this subscription
        </p>
      </div>

      {payments.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
          <p className="text-gray-500">No payments have been made to this subscription yet.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {payments.map((payment) => (
            <div key={payment.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {getStatusIcon(payment.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900">
                        Invoice #{payment.invoiceNumber}
                      </p>
                      <span className={getStatusBadge(payment.status)}>
                        {payment.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {formatDate(payment.createdAt)}
                      </div>
                      {payment.user && (
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          {payment.user.firstName} {payment.user.lastName}
                        </div>
                      )}
                      <div className="flex items-center">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="ml-1 capitalize">
                          {payment.paymentMethod.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    {payment.notes && (
                      <p className="text-sm text-gray-600 mt-1">{payment.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(payment.total)}
                  </p>
                  {payment.tax > 0 && (
                    <p className="text-sm text-gray-500">
                      Tax: {formatCurrency(payment.tax)}
                    </p>
                  )}
                  {payment.discount > 0 && (
                    <p className="text-sm text-gray-500">
                      Discount: {formatCurrency(payment.discount)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {payments.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Total Payments:</span>
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(payments.reduce((sum, payment) => sum + Number(payment.total), 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPayments;
